/**
 * Anchor Resolver — único ponto de decisão de âncora de execução.
 *
 * Responsável por:
 *  - Resolver a coluna anchor correta por modo (PPU/EAP/UBS)
 *  - Resolver a coluna usada em duplicate-check
 *  - Garantir mapeamento PPU↔planilha_item_id, EAP↔cronograma_atividade_id, UBS↔os_item_id
 *
 * REGRA: PROIBIDO `if (mode === ...)` decidindo âncora fora deste arquivo.
 */

export type ExecucaoMode = 'ppu' | 'ubs' | 'eap';

export type ExecucaoAnchor =
  | { planilha_item_id: string;     os_item_id?: never; cronograma_atividade_id?: never }
  | { os_item_id: string;           planilha_item_id?: never; cronograma_atividade_id?: never }
  | { cronograma_atividade_id: string; planilha_item_id?: never; os_item_id?: never };

export type AnchorColumn =
  | 'planilha_item_id'
  | 'os_item_id'
  | 'cronograma_atividade_id';

const COLUMN_BY_MODE: Record<ExecucaoMode, AnchorColumn> = {
  ppu: 'planilha_item_id',
  eap: 'cronograma_atividade_id',
  ubs: 'os_item_id',
};

const TIPO_ORIGEM_BY_MODE: Record<ExecucaoMode, 'ppu' | 'eap' | 'ubs'> = {
  ppu: 'ppu',
  eap: 'eap',
  ubs: 'ubs',
};

export function resolveAnchorColumn(mode: ExecucaoMode): AnchorColumn {
  return COLUMN_BY_MODE[mode];
}

/** Coluna usada para duplicate-check no insert recente (mesma do anchor). */
export function resolveDuplicateCheckColumn(mode: ExecucaoMode): AnchorColumn {
  return COLUMN_BY_MODE[mode];
}

export function resolveTipoOrigem(mode: ExecucaoMode) {
  return TIPO_ORIGEM_BY_MODE[mode];
}

/** Constrói o objeto anchor (exatamente uma chave) a partir de mode + itemId. */
export function resolveAnchor(mode: ExecucaoMode, itemId: string): ExecucaoAnchor {
  if (!itemId) throw new Error(`[anchorResolver] itemId vazio para mode=${mode}`);
  const col = COLUMN_BY_MODE[mode];
  return { [col]: itemId } as ExecucaoAnchor;
}

/** Detecta o modo a partir de um payload já com âncora — usado para validação. */
export function detectMode(payload: Record<string, unknown>): ExecucaoMode | null {
  const has = (k: string) => payload[k] != null && payload[k] !== '';
  if (has('planilha_item_id') && !has('os_item_id') && !has('cronograma_atividade_id')) return 'ppu';
  if (has('os_item_id') && !has('planilha_item_id') && !has('cronograma_atividade_id')) return 'ubs';
  if (has('cronograma_atividade_id') && !has('planilha_item_id') && !has('os_item_id')) return 'eap';
  return null;
}
